/**
 * Label helpers for the Revenue Leakage page (Leakage.tsx): the months
 * dropdown's date-range labels, the excluded-current-month note, and the
 * YoY change / "no LY sales" text shown in the metric cells.
 *
 * The trailing-months date math (subMonths, firstOfMonth) mirrors the api
 * repo's tools/retail/src/omni-leakage.ts exactly. It is copied, not
 * imported, because the UI bundle cannot pull in server-side @api/* modules.
 * Keep the two in sync by hand: omni-leakage.test.ts (api) and
 * leakageLabels.test.ts (here) cover the same calendar-month edge cases, so a
 * drift between the two shows up as a failing test on whichever side goes
 * stale first.
 */

import { formatDollars } from './useRetailStores';

// ---------------------------------------------------------------------------
// Calendar-month date math (mirrors api/tools/retail/src/omni-leakage.ts)
// ---------------------------------------------------------------------------

function parseDate(s: string): Date {
  const d = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) throw new Error(`Invalid date string: ${s}`);
  return d;
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Subtract n calendar months from a YYYY-MM-DD date, clamping the day to the
 * target month's last day (so subtracting 1 month from 2026-03-31 yields
 * 2026-02-28, not a rollover into March). A negative n adds months.
 */
export function subMonths(s: string, n: number): string {
  const d = parseDate(s);
  const day = d.getUTCDate();
  const targetFirst = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - n, 1));
  const y = targetFirst.getUTCFullYear();
  const m = targetFirst.getUTCMonth();
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return formatDate(new Date(Date.UTC(y, m, Math.min(day, lastDay))));
}

/** The first day (YYYY-MM-01) of the calendar month containing s. */
export function firstOfMonth(s: string): string {
  const d = parseDate(s);
  return formatDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
}

// ---------------------------------------------------------------------------
// Month-year labels
// ---------------------------------------------------------------------------

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2025-12-15" -> "Dec 2025". Used inside the months-option range labels. */
export function formatMonthYearShort(s: string): string {
  const [y, m] = s.split('-').map(Number);
  if (!y || !m) return s;
  return `${MONTH_ABBR[m - 1]} ${y}`;
}

/** "2026-08-05" -> "August 2026". Used for the excluded-current-month note. */
export function formatMonthYearLong(s: string): string {
  const [y, m] = s.split('-').map(Number);
  if (!y || !m) return s;
  return `${MONTH_FULL[m - 1]} ${y}`;
}

// ---------------------------------------------------------------------------
// Months dropdown
// ---------------------------------------------------------------------------

/**
 * Labels one months-dropdown option with the actual calendar range it
 * covers, e.g. "8 months (Dec 2025 - Jul 2026)". `asOf` is the report's
 * anchor date (the latest COMPLETE calendar month end; null before any data
 * has loaded). Falls back to a bare "N months" when asOf is not yet known, so
 * the dropdown renders on the first paint instead of waiting on the fetch.
 */
export function leakageMonthsOptionLabel(asOf: string | null, months: number): string {
  if (!asOf) return `${months} months`;
  const tyStart = firstOfMonth(subMonths(asOf, months - 1));
  return `${months} months (${formatMonthYearShort(tyStart)} - ${formatMonthYearShort(asOf)})`;
}

/**
 * The name of the calendar month the report deliberately excludes because it
 * is still in progress, e.g. "August 2026". `asOf` is the report's anchor
 * (the latest complete month); the excluded month is the one right after it.
 * Null before any data has loaded, since there is no anchor to work from yet.
 */
export function excludedMonthLabel(asOf: string | null): string | null {
  if (!asOf) return null;
  const excludedMonthStart = firstOfMonth(subMonths(asOf, -1));
  return formatMonthYearLong(excludedMonthStart);
}

// ---------------------------------------------------------------------------
// $ change / "no LY sales" cell text
// ---------------------------------------------------------------------------

/** Shown in the YoY change and Comp % cells when there is no last-year baseline to compare against. */
export const NO_LY_SALES_LABEL = 'No LY sales';

/**
 * Formats the YoY change in dollars for display: positive is growth (this
 * year above last year, shown with a leading "+"), negative is decline (this
 * year below last year, shown with the currency formatter's own leading
 * "-"). `decline` is the API's ly - ty metric; the change shown here is the
 * sign-flipped ty - ly, so growth reads as a positive dollar figure instead
 * of a negative decline.
 *
 * Callers should check comp === null first and render NO_LY_SALES_LABEL
 * instead of calling this, since a null comp means there is no last-year
 * baseline to change against and the dollar figure would be meaningless.
 */
export function formatYoyChangeAmount(decline: number | null): string {
  if (decline == null) return '--';
  // `|| 0` turns a -0 result (decline === 0) back into a plain 0, since
  // Intl's currency formatter renders negative zero as "-$0".
  const change = -decline || 0;
  return change > 0 ? `+${formatDollars(change)}` : formatDollars(change);
}
